import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orderService } from './index';

// Query Keys
export const orderQueryKeys = {
  all: ['orders'],
  lists: () => [...orderQueryKeys.all, 'list'],
  list: (filters) => [...orderQueryKeys.lists(), { filters }],
  details: () => [...orderQueryKeys.all, 'detail'],
  detail: (id) => [...orderQueryKeys.details(), id],
  user: (userId) => [...orderQueryKeys.all, 'user', userId],
  shop: (shopId) => [...orderQueryKeys.all, 'shop', shopId],
  owner: (ownerId) => [...orderQueryKeys.all, 'owner', ownerId],
};

// Queries
export const useOrders = (filters = {}) => {
  return useQuery({
    queryKey: orderQueryKeys.list(filters),
    queryFn: () => orderService.getAllOrders(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useOrder = (orderId) => {
  return useQuery({
    queryKey: orderQueryKeys.detail(orderId),
    queryFn: () => orderService.getOrderById(orderId),
    enabled: !!orderId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useUserOrders = (userId) => {
  return useQuery({
    queryKey: orderQueryKeys.user(userId),
    queryFn: () => {
      console.log('useUserOrders fetching orders for userId:', userId);
      return orderService.getOrdersByUser(userId);
    },
    enabled: !!userId,
    staleTime: 0, // Disable caching temporarily for debugging
    refetchOnWindowFocus: true,
  });
};

export const useShopOrders = (shopId) => {
  return useQuery({
    queryKey: orderQueryKeys.shop(shopId),
    queryFn: () => orderService.getOrdersByShop(shopId),
    enabled: !!shopId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useOrdersByOwner = (ownerId) => {
  return useQuery({
    queryKey: orderQueryKeys.owner(ownerId),
    queryFn: () => orderService.getOrdersByOwner(ownerId),
    enabled: !!ownerId,
    staleTime: 2 * 60 * 1000,
  });
};

// Mutations
export const useCreateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderData) => orderService.createOrder(orderData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.all });
      if (variables.userId) {
        queryClient.invalidateQueries({ queryKey: orderQueryKeys.user(variables.userId) });
      }
      if (variables.shopId) {
        queryClient.invalidateQueries({ queryKey: orderQueryKeys.shop(variables.shopId) });
      }
    },
    onError: (error) => {
      console.error('Order creation failed:', error);
    },
  });
};

export const useCreateOrderFromCart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderData) => orderService.createOrderFromCart(orderData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.all });
      if (variables.userId) {
        queryClient.invalidateQueries({ queryKey: orderQueryKeys.user(variables.userId) });
      }
      if (variables.items && variables.items.length > 0 && variables.items[0].shopId) {
        queryClient.invalidateQueries({ queryKey: orderQueryKeys.shop(variables.items[0].shopId) });
      }
    },
    onError: (error) => {
      console.error('Error creating order from cart:', error);
    },
  });
};

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, orderData }) => orderService.updateOrder(orderId, orderData),
    onSuccess: (data, variables) => {
      const { orderId } = variables;

      // Update the specific order in cache
      queryClient.setQueryData(orderQueryKeys.detail(orderId), data);

      queryClient.invalidateQueries({ queryKey: orderQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.user() });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.shop() });
    },
    onError: (error) => {
      console.error('Order update failed:', error);
    },
  });
};

export const useCancelOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, updatedBy }) => orderService.cancelOrder(orderId, updatedBy),
    onSuccess: (data, { orderId }) => {
      // Update the specific order in cache
      queryClient.setQueryData(orderQueryKeys.detail(orderId), data);

      queryClient.invalidateQueries({ queryKey: orderQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.user() });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.shop() });
    },
    onError: (error) => {
      console.error('Order cancellation failed:', error);
    },
  });
};

export const useRefundOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId) => orderService.refundOrder(orderId),
    onSuccess: (data, orderId) => {
      // Update the specific order in cache
      queryClient.setQueryData(orderQueryKeys.detail(orderId), data);

      queryClient.invalidateQueries({ queryKey: orderQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.user() });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.shop() });
    },
    onError: (error) => {
      console.error('Order refund failed:', error);
    },
  });
};

export const useUpdateOrderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, status }) => orderService.updateOrderStatus(orderId, status),
    onSuccess: (data, variables) => {
      const { orderId } = variables;

      // Update the specific order in cache
      queryClient.setQueryData(orderQueryKeys.detail(orderId), data);

      queryClient.invalidateQueries({ queryKey: orderQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.user() });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.shop() });
    },
    onError: (error) => {
      console.error('Order status update failed:', error);
    },
  });
};

// Combined hook for order management operations
export const useOrderManagement = () => {
  const ordersQuery = useOrders();
  const createMutation = useCreateOrder();
  const updateMutation = useUpdateOrder();
  const cancelMutation = useCancelOrder();
  const refundMutation = useRefundOrder();
  const updateStatusMutation = useUpdateOrderStatus();

  return {
    // Queries
    orders: ordersQuery.data || [],
    isLoading: ordersQuery.isLoading,
    error: ordersQuery.error,

    // Mutations
    createOrder: createMutation.mutateAsync,
    updateOrder: updateMutation.mutateAsync,
    cancelOrder: cancelMutation.mutateAsync,
    refundOrder: refundMutation.mutateAsync,
    updateOrderStatus: updateStatusMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isCancelling: cancelMutation.isPending,
    isRefunding: refundMutation.isPending,
    isUpdatingStatus: updateStatusMutation.isPending,

    // Refetch function
    refetch: ordersQuery.refetch,
  };
};
